import { useEffect, useState } from "react";
import { Button, Notice } from "@omnabase/ui";
import { useSession } from "./store";
import { HELP_ARTICLE, pushHintDue, hintSeen, rememberHintSeen } from "../lib/pushHint";
import { currentSubscription, listBrowsers, permissionNow, pushSupported, rememberedRow, turnOn } from "../lib/push";

/** Browser notifications round two (2026-10-18; brief rule 1, DECISIONS "2026-10-18 — Browser notifications, round two"): the one-time hint.
 *  On a screen whose trigger fired (a ticket a colleague just assigned to the person or mentioned them on, a direct message or a mention in
 *  chat), in a browser that can subscribe, has not blocked notifications and is not on in this workspace, for a person who is not away: one quiet
 *  line with "Turn on" (round one's flow, inside the click), "Not now" and the help article. Once shown it is remembered in this browser for
 *  this workspace, whatever the person does with it. The screen keys it by what it shows, so the trigger is read once per ticket or room.
 *  The device notifications round (2026-11-01): its words speak of notifications, never pop-ups (the sentence, the link, the toast). */
export function PushHint({ trigger }: { trigger: boolean }) {
  const { workspace, toast } = useSession();
  const slug = workspace.slug, away = workspace.me.away;
  const [armed, setArmed] = useState(false); // the trigger, kept once fired (a reload of the screen's data reads it as old news)
  const [key, setKey] = useState<string | null>(null); // shown, with the server's key "Turn on" subscribes with
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (trigger) setArmed(true); }, [trigger]);
  useEffect(() => {
    if (!armed) return;
    let live = true;
    void publicKeyIfDue(slug, away).then((k) => { if (!live || !k) return; rememberHintSeen(slug); setKey(k); });
    return () => { live = false; };
  }, [armed, slug, away]);
  if (!key) return null;
  const on = () => {
    setBusy(true);
    void turnOn(slug, key) // the permission is asked first, inside this click
      .then((r) => { if (r.ok) { setKey(null); toast("Notifications are on here", { kind: "success" }); } else if (r.reason === "denied") setKey(null); })
      .catch(() => toast("That did not work. Try again in Settings › Notifications.", { kind: "error" }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="ob-push-hint" role="status">
      <Notice icon="bell" action={<span className="ob-push-hint__actions">
        <Button size="sm" variant="primary" busy={busy} onClick={on}>Turn on</Button>
        <Button size="sm" disabled={busy} onClick={() => setKey(null)}>Not now</Button>
        <a className="ob-link ob-push-hint__link" href={HELP_ARTICLE} target="_blank" rel="noopener noreferrer">About notifications</a>
      </span>}>Get a notification next time this happens.</Notice>
    </div>
  );
}

/** the server's public key when the hint is due in this browser and workspace, else null (no key pair, cannot subscribe, blocked, on
 *  already, away, or shown before) */
async function publicKeyIfDue(slug: string, away: boolean): Promise<string | null> {
  if (away || !pushSupported() || hintSeen(slug)) return null;
  const [listing, sub, permission] = await Promise.all([listBrowsers(slug).catch(() => null), currentSubscription().catch(() => null), permissionNow()]);
  if (!listing?.publicKey) return null;
  const own = rememberedRow(slug);
  const subscribed = !!sub && !!own && listing.subscriptions.some((s) => s.id === own);
  return pushHintDue({ supported: true, permission, subscribed, away, seen: hintSeen(slug) }) ? listing.publicKey : null;
}
